import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Masonry from 'react-masonry-css';
import { PlusIcon, XMarkIcon, EyeIcon, HeartIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Portfolio } from '../../types';
import { useAppDispatch } from '../../hooks/redux';
import { deletePortfolioItem } from '../../store/slices/userSlice';
import AddPortfolioModal from './AddPortfolioModal';
import PortfolioViewModal from './PortfolioViewModal';
import EditPortfolioModal from './EditPortfolioModal';
import CollabViewModal from './CollabViewModal';
import ImageViewerModal from '../common/ImageViewerModal';
import { resolveImageUrl } from '../../utils/image';

interface MasonryCollabGridProps {
  collabImages: string[];
  isOwnProfile: boolean;
  onImageClick?: (index: number) => void;
}

const MasonryCollabGrid: React.FC<MasonryCollabGridProps> = ({ collabImages, isOwnProfile, onImageClick }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [imageHeights, setImageHeights] = useState<Record<string, number>>({});
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(null);

  const dispatch = useAppDispatch();

  // Masonry breakpoint configuration
  const breakpointColumnsObj = {
    default: 3,    // 4 columns on large screens
    1100: 2,       // 3 columns on medium screens
    700: 1,        // 2 columns on small screens
    500: 1         // 1 column on mobile
  };

  // Load image to get natural height for better masonry layout
  useEffect(() => {
    collabImages.forEach(image_url => {
      if (!imageHeights[image_url]) {
        const img = new Image();
        img.onload = () => {
          const aspectRatio = img.naturalHeight / img.naturalWidth;
          // Calculate height for a base width of 300px
          const calculatedHeight = Math.floor(300 * aspectRatio);
          setImageHeights(prev => ({
            ...prev,
            [image_url]: calculatedHeight
          }));
        };
        img.src = resolveImageUrl(image_url);
      }
    });
  }, [collabImages, imageHeights]);

  const handleAddPortfolio = () => {
    setShowAddModal(true);
  };

  // Create items array with add button for own profile
  const masonryItems = [];
  
  // Add "Add New" item at the beginning for own profile
  if (isOwnProfile) {
    masonryItems.push({
      id: 'add-new',
      type: 'add',
      component: (
        <motion.div
          key="add-new"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleAddPortfolio}
          className="group cursor-pointer bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-3xl overflow-hidden hover:border-primary-400 dark:hover:border-primary-500 hover:from-primary-50 hover:to-primary-100 dark:hover:from-primary-900/20 dark:hover:to-primary-900/40 transition-all duration-300"
          style={{ height: '280px' }} // Fixed height for add button
        >
          <div className="h-full flex flex-col items-center justify-center p-6 text-center">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-full shadow-sm group-hover:shadow-md transition-shadow mb-4 group-hover:scale-110 transform transition-transform">
              <PlusIcon className="h-10 w-10 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 transition-colors" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors mb-2">
              Add New Work
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-300 transition-colors">
              Share your latest creation
            </p>
          </div>
        </motion.div>
      )
    });
  }

  // Add portfolio items
  collabImages.forEach((image_url, index) => {
    const itemHeight = imageHeights[image_url] || 300; // Default height while loading
    
    masonryItems.push({
      id: image_url,
      type: 'collabImages',
      component: (
        <motion.div
          key={image_url}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: (index + (isOwnProfile ? 1 : 0)) * 0.1 }}
          className="group cursor-pointer relative"
          onClick={() => {
            if (onImageClick) {
              onImageClick(index);
            } else {
              setSelectedImageIndex(index);
            }
          }}
        >
          <div 
            className="relative overflow-hidden rounded-3xl bg-gray-100 dark:bg-gray-800 shadow-sm hover:shadow-xl transition-all duration-500"
            style={{ height: `${itemHeight}px` }}
          >
            <img
              src={resolveImageUrl(image_url)}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              onError={(e) => {
                console.error('❌ Image failed to load:', image_url);
              }}
            />
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Content Overlay */}
            <div className="absolute inset-0 flex flex-col justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {/* Top Actions (Owner Only) */}
              {isOwnProfile && (
                <div className="flex justify-end space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Edit functionality
                    }}
                    className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
                  >
                    <PencilIcon className="h-4 w-4 text-white" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Delete functionality
                    }}
                    className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-red-500/80 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4 text-white" />
                  </motion.button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )
    });
  });

  return (
    <div>
      {/* Masonry Layout */}
      <Masonry
        breakpointCols={breakpointColumnsObj}
        className="masonry-grid"
        columnClassName="masonry-grid_column"
      >
        {masonryItems.map(item => item.component)}
      </Masonry>

      {/* Add Portfolio Modal */}
      {showAddModal && (
        <AddPortfolioModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => setShowAddModal(false)}
        />
      )}

      {/* Image Viewer Modal */}
      {selectedImageIndex !== null && (
        <ImageViewerModal
          images={collabImages}
          thumbnailImages={collabImages}
          mediumImages={collabImages}
          initialIndex={selectedImageIndex}
          onClose={() => setSelectedImageIndex(null)}
        />
      )}
    </div>
  );
};

export default MasonryCollabGrid;
